import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { deleteDepartment, getDepartments, getDepartmentTree } from '../../../../api/endpoints/corporate';
import type { Department, DepartmentTreeNode } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { DepartmentFormDialog } from '../DepartmentFormDialog';
import { DepartmentReparentDialog } from '../DepartmentReparentDialog';
import { DepartmentOrgChartView } from '../OrgChartView';

/** Flattens the parent-linked list into a depth-annotated, DFS-ordered array so it can be
 *  rendered as an indented plain Table (no @mui/x-data-grid / tree-view dependency — see
 *  other modules for the TS 4.9.5 incompatibility note on DataGrid). Orphaned rows (a
 *  parentDepartmentId pointing at a non-existent or already-visited id) are still appended
 *  at depth 0 rather than silently dropped, so a bad backend record never hides data. */
function flattenTree(rows: Department[]): { row: Department; depth: number }[] {
  const byParent = new Map<number | null, Department[]>();
  for (const row of rows) {
    const key = row.parentDepartmentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row);
  }
  const result: { row: Department; depth: number }[] = [];
  const visited = new Set<number>();

  function walk(parentId: number | null, depth: number) {
    const children = (byParent.get(parentId) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      result.push({ row: child, depth });
      walk(child.id, depth + 1);
    }
  }
  walk(null, 0);

  for (const row of rows) {
    if (!visited.has(row.id)) {
      visited.add(row.id);
      result.push({ row, depth: 0 });
    }
  }
  return result;
}

/** Self + all descendants of `rootId` — used to keep the parent picker from letting a
 *  department become its own ancestor (a cycle). */
function collectDescendantIds(rows: Department[], rootId: number): Set<number> {
  const byParent = new Map<number | null, number[]>();
  for (const row of rows) {
    const key = row.parentDepartmentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row.id);
  }
  const result = new Set<number>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop() as number;
    for (const childId of byParent.get(id) ?? []) {
      if (!result.has(childId)) {
        result.add(childId);
        stack.push(childId);
      }
    }
  }
  return result;
}

/** Flatten the server-built `CorporateDepartment/GetTree` hierarchy back into the
 *  parent-linked `Department[]` shape the rest of this page (org chart, indented list,
 *  cycle guard) already consumes — synthesizing `parentDepartmentId` from the nesting. */
function treeToRows(nodes: DepartmentTreeNode[], parentId: number | null = null): Department[] {
  const out: Department[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, name: node.name, isActive: node.isActive, parentDepartmentId: parentId });
    if (node.children?.length) out.push(...treeToRows(node.children, node.id));
  }
  return out;
}

export default function DepartmentListPage() {
  const [view, setView] = useState<'list' | 'chart'>('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [reparenting, setReparenting] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState<Department | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Server tree is the primary source; the flat `GetDepartments` list is the fallback when
  // `GetTree` fails. Tree key is nested under `['corporate','departments']` so the existing
  // `invalidateQueries(['corporate','departments'])` in the dialogs refreshes both.
  const treeQuery = useQuery({
    queryKey: ['corporate', 'departments', 'tree'],
    queryFn: () => getDepartmentTree(),
    retry: false,
  });
  const listQuery = useQuery({
    queryKey: ['corporate', 'departments'],
    queryFn: () => getDepartments(),
    enabled: treeQuery.isError,
  });
  const query = {
    isError: treeQuery.isError && listQuery.isError,
    error: listQuery.error ?? treeQuery.error,
    isFetching: treeQuery.isFetching || listQuery.isFetching,
  };
  const rows: Department[] = useMemo(
    () =>
      treeQuery.data?.tree
        ? treeToRows(treeQuery.data.tree)
        : listQuery.data?.departments ?? [],
    [treeQuery.data, listQuery.data]
  );

  const flattened = useMemo(() => flattenTree(rows), [rows]);
  const options = useMemo(
    () => flattened.map(({ row, depth }) => ({ id: row.id, label: `${'— '.repeat(depth)}${row.name}` })),
    [flattened]
  );
  const excludeIds = useMemo(
    () => (editing || reparenting ? collectDescendantIds(rows, (editing ?? reparenting)!.id) : new Set<number>()),
    [rows, editing, reparenting]
  );

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteDepartment(id),
    onSuccess: () => {
      enqueueSnackbar('Departman silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'departments'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Departmanlar</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Departman
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <Tabs value={view} onChange={(_e, value) => setView(value)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab value="list" label="Liste" sx={{ minHeight: 36 }} />
        <Tab value="chart" label="Organizasyon Şeması" sx={{ minHeight: 36 }} />
      </Tabs>

      {view === 'chart' && (
        <Paper variant="outlined">
          <DepartmentOrgChartView rows={rows} onNodeClick={(department) => setEditing(department)} />
        </Paper>
      )}

      {view === 'list' && (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Departman Adı</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {flattened.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı departman bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {flattened.map(({ row, depth }) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ pl: 2 + depth * 3 }}>{row.name}</TableCell>
                <TableCell>
                  <Chip size="small" color={row.isActive ? 'success' : 'default'} label={row.isActive ? 'Aktif' : 'Pasif'} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Üst Departmanı Değiştir">
                    <IconButton size="small" onClick={() => setReparenting(row)} aria-label="Taşı">
                      <DriveFileMoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <DepartmentFormDialog
        open={createOpen}
        department={null}
        options={options}
        excludeIds={new Set()}
        onClose={() => setCreateOpen(false)}
      />
      <DepartmentFormDialog
        open={!!editing}
        department={editing}
        options={options}
        excludeIds={excludeIds}
        onClose={() => setEditing(null)}
      />
      <DepartmentReparentDialog
        open={!!reparenting}
        department={reparenting}
        options={options}
        excludeIds={excludeIds}
        onClose={() => setReparenting(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Departmanı sil"
        description={`"${deleting?.name ?? ''}" departmanını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
